import React, { useCallback, useMemo } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  List as ListIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import DobbyGenericPage from "../components/DobbyGenericPage";
import { ShipmentTimelineComponent } from "./ShipmentTimelineComponent";
import { EmailThreadComponent } from "../components/EmailThreadComponent";
import {
  openEmailPopup,
  closeEmailPopup,
  openShipmentSidebar,
  closeShipmentSidebar,
} from "../store/shipmentSlice";
import type { RootState } from "../store/rootReducer";
import type { MenuItem } from "../components/DobbyGenericPage";
import type { ShipmentUpdate, EmailSource } from "@shared/shipments/timelineWithThreadLinesComponent";
import { TimelineSidePanel } from "../admin/shipments/TimelineSidePanel";

interface ShipmentPageChildProps {
  onOpenEmail?: (emailId: string, eventName: string) => void;
  onShipmentClick?: (
    shipmentId: string,
    updates?: ShipmentUpdate[],
    emailSource?: EmailSource
  ) => void;
}

interface DobbyShipmentPageProps {
  children: React.ReactElement<ShipmentPageChildProps>;
  title: string;
}

export const DobbyShipmentPage: React.FC<DobbyShipmentPageProps> = ({
  children,
  title,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const emailDialog = useSelector(
    (state: RootState) => state.shipment.emailDialog
  );
  const sidebar = useSelector((state: RootState) => state.shipment.sidebar);
  const isAdmin = useSelector((state: RootState) => state.userData.user?.isAdmin ?? false);

  // Get tag from query parameter to preserve it in navigation
  const searchParams = new URLSearchParams(location.search);
  const tag = searchParams.get("tag");
  const tagQuery = tag ? `?tag=${tag}` : "";

  // Define navigation menus (memoized to avoid recreation on every render)
  const menus: MenuItem[] = useMemo(
    () => [
      {
        label: "Tracking List",
        value: "list",
        icon: <ListIcon />,
        href: `/shipments${tagQuery}`,
      },
      {
        label: "Reminders Dashboard",
        value: "exceptions",
        icon: <NotificationsIcon />,
        href: `/shipments/reminders-dashboard${tagQuery}`,
      },
    ],
    [tagQuery]
  );

  // Determine current menu value based on location
  const currentMenuValue =
    location.pathname === "/shipments"
      ? "list"
      : location.pathname === "/shipments/reminders-dashboard"
        ? "exceptions"
        : "list";

  // Handle email opening
  const handleOpenEmail = useCallback(
    (emailId: string, eventName: string) => {
      dispatch(openEmailPopup({ emailId, eventName }));
    },
    [dispatch]
  );

  // Handle email dialog close
  const handleCloseEmail = useCallback(() => {
    dispatch(closeEmailPopup());
  }, [dispatch]);

  // Handle shipment click
  const handleShipmentClick = useCallback(
    (
      shipmentId: string,
      updates?: ShipmentUpdate[],
      emailSource?: EmailSource
    ) => {
      dispatch(openShipmentSidebar({ shipmentId, updates, emailSource }));
    },
    [dispatch]
  );

  // Handle sidebar close
  const handleSidebarClose = useCallback(() => {
    dispatch(closeShipmentSidebar());
  }, [dispatch]);

  return (
    <DobbyGenericPage
      fullHeight
      title={title}
      menus={menus}
      currentMenuValue={currentMenuValue}
    >
      {/* Pass handlers to children via React.cloneElement */}
      {React.cloneElement(children, {
        onOpenEmail: handleOpenEmail,
        onShipmentClick: handleShipmentClick,
      })}

      {/* Sidebar for shipment timeline */}
      <TimelineSidePanel
        open={sidebar.isOpen}
        onClose={handleSidebarClose}
        shipmentId={sidebar.shipmentId || undefined}
        updates={sidebar.updates}
        emailSource={sidebar.emailSource}
        isAdmin={isAdmin}
      />

      {/* Email Dialog */}
      {emailDialog.isOpen && emailDialog.emailId && (
        <Dialog
          open={emailDialog.isOpen}
          onClose={handleCloseEmail}
          fullWidth
          maxWidth="lg"
          sx={{
            "& .MuiDialog-paper": {
              height: "90vh",
            },
          }}
        >
          <IconButton
            aria-label="close"
            onClick={handleCloseEmail}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent sx={{ p: 0 }}>
            <EmailThreadComponent
              emailId={emailDialog.emailId}
              sx={{ height: "100%" }}
            />
          </DialogContent>
        </Dialog>
      )}
    </DobbyGenericPage>
  );
};
